import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useError } from '../../hooks'
import Button from '../Button'
import TextField from '../Form/TextField'
import { toast } from 'react-hot-toast'
import { Eye, EyeOff } from 'lucide-react'
import ModalDialog from '../ModalDialog'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

// Validation Schema
const signupSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  displayName: Yup.string().required('Display Name is required'),
  email: Yup.string().email('Email is invalid').required('Email is required'),
  phone: Yup.string().required('Phone Number is required'),
  password: Yup.string().required('Password is required'),
  confirmPassword: Yup.string()
    .required('Confirm Password is required')
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
})

export default function Signup() {
  const setError = useError()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: ''
  })

  const initialValues = {
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  }

  async function handleSubmit(values, { setSubmitting }) {
    const {
      firstName,
      lastName,
      displayName,
      email,
      password,
      phone,
      confirmPassword
    } = values

    setLoading(true)
    try {
      // Public signup. The server pins organisation, roles and approval for
      // this path — no orgId, no roleBindings, isApproved stays false until an
      // admin approves from the admin console. Sending them would be ignored.
      const response = await fetch(
        `https://us-central1-live-alarm-v2.cloudfunctions.net/${
          process.env.REACT_APP_ENV === 'production'
            ? 'createUserP'
            : 'createUserS'
        }`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName,
            lastName,
            displayName,
            email,
            password,
            phone
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        toast.error(data.message || 'Something went wrong.')
      } else {
        setSuccessModal({
          isOpen: true,
          message: 'Signup successful! Please wait for admin approval before logging in.'
        })
      }
    } catch (err) {
      setError(err)
      toast.error('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <div>
      <ModalDialog
        active={successModal.isOpen}
        // onClose={() => {
        //   setSuccessModal(prev => ({ ...prev, isOpen: false }))
        //   navigate('/login')
        // }}
        hideHeader={true}
      >
        <div className="text-center p-6">
          <div className="checkmark-circle mb-4">
            <div className="background"></div>
            <div className="checkmark draw">
            </div>
          </div>
          <h2 className="mb-4 text-2xl font-bold">
            Signup Successful!
          </h2>
          <p className="mb-2 !text-[16px]">
            Your registration has been submitted for admin approval.
          </p>
          <p className="mb-5 !text-[16px]">We'll let you know as soon as your profile is approved and you're all set to experience Live Alarm in action!</p>
          <p className="mb-5 !text-[20px] !text-[#293894] !font-[500]">Stay tuned, you're almost there!</p>
          <button
            onClick={() => {
              setSuccessModal(prev => ({ ...prev, isOpen: false }))
              navigate('/login')
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full transition-colors"
          >
            Go to Login
          </button>
        </div>
      </ModalDialog>
      <div className='login-container'>
        <div className='login-grid'>
          <div className='login-left login-centered'>
            <div className='login-form'>
              <div className='login-logo'>
                <img
                  className='login'
                  src='/assets/images/live-alarm.png'
                  alt='signup visual'
                />
              </div>

              <Formik
                initialValues={initialValues}
                validationSchema={signupSchema}
                onSubmit={handleSubmit}>
                {({ errors, touched, handleChange, handleBlur, values }) => (
                  <Form>
                    <div className='login-input mb-4'>
                      <div className='flex gap-2 mb-3'>
                        <div className='w-[50%]'>
                          <Field name='firstName'>
                            {({ field }) => (
                              <TextField
                                {...field}
                                label='First Name'
                                type='text'
                                required={false}
                                autoComplete='off'
                                placeholder='First Name'
                                onChange={(e) => {
                                  field.onChange(e)
                                  handleChange(e)
                                }}
                                onBlur={handleBlur}
                              />
                            )}
                          </Field>
                          {touched.firstName && errors.firstName && (
                            <div className='text-red-500 text-[12px]'>
                              {errors.firstName}
                            </div>
                          )}
                        </div>
                        <div className='w-[50%]'>
                          <Field name='lastName'>
                            {({ field }) => (
                              <TextField
                                {...field}
                                label='Last Name'
                                type='text'
                                required={false}
                                autoComplete='off'
                                placeholder='Last Name'
                                onChange={(e) => {
                                  field.onChange(e)
                                  handleChange(e)
                                }}
                                onBlur={handleBlur}
                              />
                            )}
                          </Field>
                          {touched.lastName && errors.lastName && (
                            <div className='text-red-500 text-[12px]'>
                              {errors.lastName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-3">
                        <Field name='displayName' >
                          {({ field }) => (
                            <TextField
                              {...field}
                              label='Display Name (Badge)'
                              type='text'
                              required={false}
                              autoComplete='off'
                              placeholder='Display Name'
                              onChange={(e) => {
                                field.onChange(e)
                                handleChange(e)
                              }}
                              onBlur={handleBlur}
                            />
                          )}
                        </Field>
                        {touched.displayName && errors.displayName && (
                          <div className='text-red-500 text-[12px] mb-3'>
                            {errors.displayName}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">

                        <Field name='email'>
                          {({ field }) => (
                            <TextField
                              {...field}
                              label='Email'
                              type='email'
                              required={false}
                              autoComplete='off'
                              placeholder='Email'
                              onChange={(e) => {
                                field.onChange(e)
                                handleChange(e)
                              }}
                              onBlur={handleBlur}
                            />
                          )}
                        </Field>
                        {touched.email && errors.email && (
                          <div className='text-red-500 text-[12px] mb-3'>
                            {errors.email}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">

                        <Field name='phone'>
                          {({ field }) => (
                            <TextField
                              {...field}
                              label='Phone Number'
                              type='number'
                              required={false}
                              autoComplete='off'
                              placeholder='Phone Number'
                              onChange={(e) => {
                                field.onChange(e)
                                handleChange(e)
                              }}
                              onBlur={handleBlur}
                            />
                          )}
                        </Field>
                        {touched.phone && errors.phone && (
                          <div className='text-red-500 text-[12px] mb-3'>
                            {errors.phone}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">

                        <div style={{ position: 'relative' }} >
                          <Field name='password'>
                            {({ field }) => (
                              <TextField
                                {...field}
                                label='Password'
                                type={showPassword ? 'text' : 'password'}
                                required={false}
                                autoComplete='off'
                                placeholder='Password'
                                onChange={(e) => {
                                  field.onChange(e)
                                  handleChange(e)
                                }}
                                onBlur={handleBlur}
                              />
                            )}
                          </Field>
                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: 'absolute',
                              right: '12px',
                              top: '34px',
                              cursor: 'pointer',
                              color: '#555'
                            }}>
                            {showPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </span>
                        </div>
                        {touched.password && errors.password && (
                          <div className='text-red-500 text-[12px] mb-3'>
                            {errors.password}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <div style={{ position: 'relative', marginTop: '16px' }}>
                          <Field name='confirmPassword'>
                            {({ field }) => (
                              <TextField
                                {...field}
                                label='Confirm Password'
                                type={showConfirmPassword ? 'text' : 'password'}
                                required={false}
                                autoComplete='off'
                                placeholder='Confirm Password'
                                onChange={(e) => {
                                  field.onChange(e)
                                  handleChange(e)
                                }}
                                onBlur={handleBlur}
                              />
                            )}
                          </Field>
                          <span
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            style={{
                              position: 'absolute',
                              right: '12px',
                              top: '34px',
                              cursor: 'pointer',
                              color: '#555'
                            }}>
                            {showConfirmPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </span>
                        </div>
                        {touched.confirmPassword && errors.confirmPassword && (
                          <div className='text-red-500 text-[12px] mb-3'>
                            {errors.confirmPassword}
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      type='submit'
                      className='login-button'
                      disabled={loading}>
                      {loading ? 'Signing up...' : 'Complete Signup'}
                    </Button>

                    <div className='login-signup'>
                      <label className='login-label'>
                        Already have an account?
                      </label>
                      <a
                        href='/login'
                        className='login-forgot'
                        style={{ textDecoration: 'none' }}>
                        <strong>Login</strong>
                      </a>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <div className='login-right login-centered login-overflow'>
            <img
              className='login-image'
              src='/assets/images/logo.png'
              alt='signup visual'
            />
          </div>
        </div>
      </div>
    </div>
  )
}
